import {
  AgxAvatarLabelGroup,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  Breakpoints,
  IColumnConfig,
} from '@urbanx/agx.ui-components2';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import { AgencyAgentModel } from '@urbanx/agx.agencies.client';
import dayjs from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { CampaignTableColumnNames, SearchableCampaign } from 'types/campaigns';
import { Pages } from 'constants/menu';
import { formatCampaignStageLabel } from 'utils/campaignUtils';
import { getStatusConfig } from './components/campaignMarketing/CampaignFlowStatusBadge';
import CampaignOptions from './components/options/CampaignOptions';
import { CampaignActionRequestCampaignAction } from '@urbanx/agx.campaigns.client';

interface CampaignsTableProps {
  agencyId: string;
  campaigns: SearchableCampaign[];
  agents: AgencyAgentModel[];
  isLoading: boolean;
  isArchivedTab: boolean;
  onPerformCampaignAction: (
    campaignId: string,
    campaignAction: CampaignActionRequestCampaignAction
  ) => void;
}

const campaignsTableHeaders: IColumnConfig[] = [
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Campaign,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Status,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.LeadAgent,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.SecondAgent,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Marketing,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Contract,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Launch,
  },
  {
    align: 'left',
    onClick: (event) => event.stopPropagation(),
    name: CampaignTableColumnNames.Action,
  },
];

export function CampaignsTable({
  agencyId,
  campaigns,
  agents,
  isLoading,
  isArchivedTab,
  onPerformCampaignAction,
}: CampaignsTableProps) {
  const navigate = useNavigate();
  const { userAccount } = useContext(AuthContext);
  const userRoles = (userAccount?.roles as StaffRole[]) || [];

  const campaignColumns = useMemo(() => {
    if (!campaigns) return [];

    return campaigns?.map((campaign, index) => {
      const [addressLineOne, addressLineTwo] = campaign.cleanAddress;
      const leadAgent = agents.find((agent) => agent.id === campaign.leadAgentId);
      const secondAgent = agents.find((agent) => agent.id === campaign.secondAgentId);
      const isLaunched = campaign.filterableLaunchDate?.isBefore(dayjs(), 'day');
      const launchDateToday = campaign.filterableLaunchDate
        ? campaign.filterableLaunchDate.isSame(dayjs(), 'day')
        : false;
      const marketingStatusConfig = getStatusConfig(campaign.marketingStatus);

      return {
        [CampaignTableColumnNames.Campaign]: (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`campaign-${index}-image`}
              label1={(addressLineOne ?? '') + (addressLineTwo ? `,` : '')}
              label2={addressLineTwo ?? ''}
              photoUrl={campaign.propertyImageUrls?.thumbnail ?? placeHolderImage}
              type="photo"
              variant="round"
              size="large"
            />
          </AgxSpacer>
        ),
        [CampaignTableColumnNames.Status]: (
          <AgxTypography
            id={`campaign-${index}-status`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {campaign.stage ? formatCampaignStageLabel(campaign.stage) : '--'}
          </AgxTypography>
        ),
        [CampaignTableColumnNames.LeadAgent]: campaign.leadAgentId ? (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`lead-agent-${index}`}
              label1={campaign.leadAgentName ?? 'Unassigned'}
              type="avatar"
              variant="round"
              size="small"
              photoUrl={leadAgent?.photoUrl ?? undefined}
              responsiveBreakpoint={Breakpoints.Laptop}
            />
          </AgxSpacer>
        ) : (
          '--'
        ),
        [CampaignTableColumnNames.SecondAgent]: campaign.secondAgentId ? (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`second-agent-${index}`}
              label1={campaign.secondAgentName ?? 'Unassigned'}
              type="avatar"
              variant="round"
              size="small"
              photoUrl={secondAgent?.photoUrl ?? undefined}
              responsiveBreakpoint={Breakpoints.Laptop}
            />
          </AgxSpacer>
        ) : (
          '--'
        ),
        [CampaignTableColumnNames.Marketing]:
          campaign.marketingStatus && marketingStatusConfig ? (
            <AgxBadge
              type="colourfill"
              colour={marketingStatusConfig.badgeColor}
              size="md"
              text={marketingStatusConfig.text}
            />
          ) : (
            '--'
          ),
        [CampaignTableColumnNames.Contract]: campaign.blankContractIsLive ? (
          <AgxBadge type="colourfill" colour="green" size="md" text="Contract Live" />
        ) : (
          <AgxBadge type="colourfill" colour="red" size="md" text="Contract Offline" />
        ),
        [CampaignTableColumnNames.Launch]: campaign.filterableLaunchDate ? (
          launchDateToday ? (
            <AgxBadge
              type="highlighted"
              variant="normal"
              size="md"
              highlightText="Today"
              text={`Launch ${campaign.filterableLaunchDate?.format('Do MMM')}`}
              colour="yellow"
            />
          ) : (
            <AgxBadge
              type="colourfill"
              variant="normal"
              size="md"
              text={`${isLaunched ? 'Launched' : 'Launch'} ${campaign.filterableLaunchDate?.format('Do MMM')}`}
              colour={isLaunched ? 'grey' : 'orange'}
            />
          )
        ) : (
          <AgxBadge
            type="colourfill"
            variant="normal"
            size="md"
            text="Launch not set"
            colour="grey"
          />
        ),
        [CampaignTableColumnNames.Action]: (
          <CampaignOptions
            key={campaign.id}
            selectedCampaign={campaign}
            onPerformCampaignAction={onPerformCampaignAction}
            isArchived={isArchivedTab}
            dataTestId={`agx-campaign-options-${index}`}
          />
        ),
      };
    });
  }, [agents, campaigns, isArchivedTab, onPerformCampaignAction]);

  const onCampaignRowClick = useCallback(
    (index: number) => {
      const campaign = campaigns?.[index];
      const page = userRoles.includes(StaffRole.WorkflowContributor)
        ? Pages.CAMPAIGN_TASKS
        : Pages.PROPERTY_INFORMATION;
      navigate(`/${Pages.AGENCIES}/${agencyId}/campaigns/${campaign?.id}/${page}`);
    },
    [agencyId, campaigns, navigate, userRoles]
  );

  return (
    <AgxTable
      columns={campaignsTableHeaders}
      rows={campaignColumns}
      variant={!isLoading && campaigns?.length ? 'default' : 'minimal'}
      showLoadingState={isLoading}
      tableStyle="square"
      noDataLabel={isLoading ? 'Loading...' : 'No campaigns found.'}
      noDataGlyphType="house"
      responsive
      onRowClick={!isLoading && campaigns?.length ? onCampaignRowClick : undefined}
    />
  );
}
